export interface IWindowDevToolHotkey {
    ctrl?: boolean;
    shift?: boolean;
    alt?: boolean;
    meta?: boolean;
    key: string;
}

export interface IWindowDevTool {
    name: string;
    run: () => void;
    hotkey?: IWindowDevToolHotkey;
}

const matchesHotkey = (event: KeyboardEvent, hotkey: IWindowDevToolHotkey): boolean => {
    const ctrlMatches = Boolean(hotkey.ctrl) === event.ctrlKey;
    const shiftMatches = Boolean(hotkey.shift) === event.shiftKey;
    const altMatches = Boolean(hotkey.alt) === event.altKey;
    const metaMatches = Boolean(hotkey.meta) === event.metaKey;
    const keyMatches = event.key.toLowerCase() === hotkey.key.toLowerCase();
    return ctrlMatches && shiftMatches && altMatches && metaMatches && keyMatches;
};

/**
 * Attach a set of dev tools to `window` and bind their optional hotkeys.
 * Each tool's `run` becomes callable as `window[tool.name]()` from the console.
 * Returns a cleanup that removes the listener and deletes the window globals.
 *
 * @example
 * const cleanup = registerWindowDevTools([
 *   { name: 'showFontPreview', run: showFontPreview, hotkey: { ctrl: true, shift: true, key: 'f' } },
 * ]);
 */
export const registerWindowDevTools = (tools: ReadonlyArray<IWindowDevTool>): (() => void) => {
    if (typeof window === 'undefined') {
        return (): void => undefined;
    }

    const globalScope = window as unknown as Record<string, unknown>;
    tools.forEach((tool) => {
        globalScope[tool.name] = tool.run;
    });

    const toolsWithHotkeys = tools.filter(
        (tool): tool is IWindowDevTool & { hotkey: IWindowDevToolHotkey } => tool.hotkey !== undefined,
    );

    const handleKeydown = (event: KeyboardEvent): void => {
        const matched = toolsWithHotkeys.find((tool) => matchesHotkey(event, tool.hotkey));
        if (matched) {
            event.preventDefault();
            matched.run();
        }
    };

    window.addEventListener('keydown', handleKeydown);

    return (): void => {
        window.removeEventListener('keydown', handleKeydown);
        tools.forEach((tool) => {
            delete globalScope[tool.name];
        });
    };
};
